import type { MarketRegime, RiskLevel } from '../types/marketAnalytics';

export interface RegimeThemeMeta {
  regime: MarketRegime;
  emoji: string;
  labelEn: string;
  labelAr: string;
  accent: string;
  accentSoft: string;
  glow: string;
}

export const REGIME_THEME: Record<MarketRegime, RegimeThemeMeta> = {
  CALM: {
    regime: 'CALM',
    emoji: '😌',
    labelEn: 'Calm',
    labelAr: 'هادئ',
    // Volatility regime is not direction (D2): calm = info blue, never green.
    accent: '#42a5f5',
    accentSoft: 'rgba(66, 165, 245, 0.12)',
    glow: 'rgba(66, 165, 245, 0.3)',
  },
  MOMENTUM: {
    regime: 'MOMENTUM',
    emoji: '🔥',
    labelEn: 'Momentum',
    labelAr: 'زخم',
    accent: '#ff9800',
    accentSoft: 'rgba(255, 152, 0, 0.16)',
    glow: 'rgba(255, 152, 0, 0.4)',
  },
  EXTREME: {
    regime: 'EXTREME',
    emoji: '⚡',
    labelEn: 'Extreme',
    labelAr: 'متطرف',
    // Extreme = deep orange (impact-5), not the down red (D2).
    accent: '#ff5722',
    accentSoft: 'rgba(255, 87, 34, 0.18)',
    glow: 'rgba(255, 87, 34, 0.45)',
  },
  SLEEPY: {
    regime: 'SLEEPY',
    emoji: '😴',
    labelEn: 'Sleepy',
    labelAr: 'خامل',
    accent: '#8b9cb3',
    accentSoft: 'rgba(139, 156, 179, 0.14)',
    glow: 'rgba(139, 156, 179, 0.25)',
  },
};

export const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  // Risk level is not direction (D2): low neutral, medium gold, high warning orange.
  LOW: '#b2b5be',
  MEDIUM: '#ffb74d',
  HIGH: '#ff9800',
};

export function regimeMeta(regime: MarketRegime): RegimeThemeMeta {
  return REGIME_THEME[regime] || REGIME_THEME.CALM;
}

export function applyRegimeDocumentTheme(regime: MarketRegime | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const meta = regime ? regimeMeta(regime) : null;
  if (!meta) {
    root.removeAttribute('data-mmec-regime');
    root.style.removeProperty('--mmec-regime-accent');
    root.style.removeProperty('--mmec-regime-accent-soft');
    root.style.removeProperty('--mmec-regime-glow');
    return;
  }
  root.setAttribute('data-mmec-regime', meta.regime);
  root.style.setProperty('--mmec-regime-accent', meta.accent);
  root.style.setProperty('--mmec-regime-accent-soft', meta.accentSoft);
  root.style.setProperty('--mmec-regime-glow', meta.glow);
}

export const REGIME_BADGE_HIDDEN_KEY = 'mmec_regime_badge_hidden';
